/**
 * Reading the certificate register (build 2).
 *
 * The certificates are library documents whose type the pack files under the
 * `certificate` category; the boards' feed reads them here. The key sits under
 * "documents", so every library write — a certificate recorded on a board, a
 * revision issued on the library screen — refreshes it with the rest of the
 * library, and LiveRefresh's blanket invalidation reaches it too. It has a key
 * of its own beyond that: the unfiltered library list and this one must never
 * share a cache entry.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import { DataSourceError } from "./dataSource";

export const certificateKeys = {
  register: (siteId: string) => ["documents", siteId, "category:certificate"] as const,
};

/** A refusal is an answer, not a fault: asking again cannot change it. */
const retryUnlessRefused = (failures: number, e: unknown): boolean =>
  !(e instanceof DataSourceError && (e.status === 401 || e.status === 403)) && failures < 1;

/** Every certificate the library holds at this site (tenant-wide ones included). */
export function useCertificateRegister(opts: { enabled: boolean }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: certificateKeys.register(site.id),
    queryFn: () => source.listDocuments({ siteId: site.id, category: "certificate" }),
    enabled: opts.enabled,
    staleTime: 30_000,
    retry: retryUnlessRefused,
  });
}
